import * as React from "react";
import {
  styled,
  createTheme,
  ThemeProvider,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import { ListItemButton, ListItemText, ListSubheader } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../app/store/configureStore";
import { useEffect } from "react";
import {
  entrezDbSelectors,
  setDbParams,
  setRecentlySelecteDBS,
} from "../app/store/entrezDbSlice";

import EntrezPage from "./entrez/EntrezPage";
import Footer from "../app/layout/Footer";
import { ToastContainer } from "react-toastify";
import TitleSecondary from "../app/components/TitleSecondary";
import { entrezConfig } from "../app/util/entrezConfig";

// function Copyright(props: any) {
//   return (
//     <Typography
//       variant="body2"
//       color="text.secondary"
//       align="center"
//       {...props}
//     >
//       {"Copyright © "}
//       <Link color="inherit" href="https://mui.com/">
//         Your Website
//       </Link>{" "}
//       {new Date().getFullYear()}
//       {"."}
//     </Typography>
//   );
// }

const drawerWidth: number = 190;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(0),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(0),
      },
    }),
  },
}));

// const useListStyles = makeStyles({
//   root: {
//     '&$selected': {
//       backgroundColor: 'red',
//       '&:hover': {
//         backgroundColor: 'yellow',
//       }
//     },
//   },
//   selected: {},
// });

const mdTheme = createTheme({
  typography: {
    // In Chinese and Japanese the characters are usually larger,
    // so a smaller fontsize may be appropriate.
    fontSize: 12,
  },
  palette: {
    // Light Bluish
    primary: {
      light: "#4dabf5",
      main: "#0abbeb",
      dark: "#089bc4",
      contrastText: "#fff",
    },
    // Purple
    secondary: {
      light: "#e6ccff",
      main: "#bd1cdc",
      dark: "#9a17b5",
      contrastText: "#000",
    },
  },
});

function DashboardContent() {
  const [open, setOpen] = React.useState(true);
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const { dbsLoaded } = useAppSelector((state) => state.entrezDb);

  const dispatch = useAppDispatch();

  //get all the databases
  const allDBsWithItsState = useAppSelector(entrezDbSelectors.selectAll);

  const handleDatabaseSelection = (selectedDB: string) => {
    console.log("Selected DB ID is: " + selectedDB);

    dispatch(setDbParams({ db: selectedDB }));
    dispatch(setRecentlySelecteDBS({ db: selectedDB }));
  };

  //setting one of the database as default database selection
  useEffect(() => {
    console.log("useEffect of default db selection from dashboard");
    if (dbsLoaded) {
      handleDatabaseSelection(entrezConfig.DefaultDB);
    }
  }, [dbsLoaded]);

  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        {/* absolute */}
        <AppBar position="fixed" open={open}>
          <Toolbar
            sx={{
              pr: "24px", // keep right padding 24px when drawer closed
              borderBottom: "5px solid #bd1cdc",
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: "36px",
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              component="h2"
              variant="h6"
              color="inherit"
              noWrap
              sx={{ flexGrow: 1 }}
            >
              NCBI Query Tool (NQT)
            </Typography>

            {/* <IconButton color="inherit">
              <Badge badgeContent={4} color="secondary">
                <NotificationsIcon />
              </Badge>
            </IconButton> */}
          </Toolbar>
        </AppBar>

        {/* List */}
        <Drawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              px: [1],
              backgroundColor: "secondary.main",
            }}
          >
            <Typography
              component="h2"
              variant="h6"
              color="white"
              noWrap
              sx={{ flexGrow: 1, ml: 4 }}
            >
              NCBI QT
            </Typography>
            <IconButton
              sx={{ color: "white", backgroundColor: "primary.main" }}
              onClick={toggleDrawer}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          {/* <List>{mainListItems}</List> */}

          <Paper
            style={{
              overflow: "auto",
              display: "flex",
              flexDirection: "row",
              marginBottom: 8,
              overflowY: "scroll",
              height: "calc(100vh - 100px)",
            }}
          >
            <List
              sx={{
                display: "flex",
                maxHeight: "100%",
                flexDirection: "column",
                paddingLeft: 1,
                marginLeft: 1,
                marginRight: 1,
              }}
            >
              {/* inset */}
              <ListSubheader>
                <div style={{ padding: 2, marginLeft: 3 }}>
                  <TitleSecondary> Select DB</TitleSecondary>
                </div>
              </ListSubheader>
              {/* style={{maxHeight: '100%', overflow: 'auto'}} */}
              {allDBsWithItsState.map(({ id, dbname, displayname }) => (
                <ListItemButton 
                  sx={{
                    margin: 1,
                    borderRadius:1,
                    textAlign: "left",
                    backgroundColor: "secondary.light",
                    "&:hover": {
                      backgroundColor: "secondary.main",
                      color: "white",
                      
                    },
                  }}
                  // classes={{ root: classes.root, selected: classes.selected }}
                  key={dbname}
                  onClick={() => handleDatabaseSelection(id)}
                >
                  {/* className={database === dbname? classes.active:null} */}

                  {/* {dbParams &&
                    dbParams.db.length > 0 &&
                    dbParams.db === dbname && (
                      <ListItemIcon>
                        <CheckIcon color="secondary" fontSize="medium" sx={{padding:0, margin:0}} />
                      </ListItemIcon>
                    )} */}
          

                  <ListItemText
                    disableTypography
                    primary={displayname}
                    sx={
                      {
                        // border: "2px solid rgba(200,200,200)",
                      }
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Drawer>

        {/* flexGrow:1 causing box to take whole space, you can set it to 0 to do the opposite */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "light"
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: "100vh",
            overflow: "auto",
          }}
        >
          <Toolbar />

          {/* Load the main Page. */}
          <EntrezPage></EntrezPage>

          {/* <Copyright sx={{ pt: 4 }} /> */}
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default function Dashboard() {
  return (
    <React.Fragment>
      {/* <Header></Header> */}
      <DashboardContent />
      <ToastContainer position="bottom-right" hideProgressBar />
      <Footer></Footer>
    </React.Fragment>
  );
}

// {/* Recently used dbs */}
//           {/* <Paper style={{ overflow: "auto" }}>
//             {recentSelectedDBs && (
//               <List>
//                 <ListSubheader inset>Recent</ListSubheader>
//                 {/* style={{maxHeight: '100%', overflow: 'auto'}} */}
//                 {recentSelectedDBs.map((dbname) => (
//                   <ListItem
//                     button
//                     key={dbname}
//                     onClick={() => handleDatabaseSelection(dbname)}
//                   >
//                     {/* className={database === dbname? classes.active:null} */}
//                     {/* <ListItemIcon>
//                   <BarChartIcon />
//                 </ListItemIcon> */}
//                     <ListItemText primary={dbname} />
//                   </ListItem>
//                 ))}
//               </List>
//             )}
//             <Divider />
//           </Paper> */}
